import { AccessRightModel, DocumentModel, NeedViewModel, PropertyViewModel } from "@/Domains";
import {
    DateFormatHelper,
    SoNumber,
    DetailItem,
    DetailSection,
    MandateEnums,
    AppEnumHelper,
    formatName,
    dateCompare,
    DOMAIN_TYPES,
    DocumentEnums,
    NeedEnums,
    PropertyEnums,
    RecordMap,
    SoVAT,
} from '@/Shared';

import { MandateModel } from '../Models/MandateModel';
import { AuditViewModel } from "@/Shared/Logic/ViewModels/AuditViewModel";
import { AuditModel } from "@/Shared/Logic/Models/AuditModel";
import { Row } from "@/Shared/UI/Components/DetailsListBox/DetailsListBox";


export interface MandateVMParams {
    mandate: MandateModel;
    viewEnums: MandateEnums;
    DocumentEnums: DocumentEnums;
    NeedEnums: NeedEnums,
    PropertyEnums: PropertyEnums,
    documentMap: RecordMap<DocumentModel>;
    isDisplaying: boolean;
    accessRight: AccessRightModel
}


/**
 * ViewModel: MandateViewModel
 *
 * Description:
 * ViewModel responsible for adapting a Mandate domain model into a format
 * consumable by UI components. This includes:
 * - Formatting dates and numbers for display
 * - Resolving human-readable labels from enums
 * - Preparing detail sections for UI rendering
 * - Wrapping audit trail information
 * - Managing associated documents
 *
 * Responsibilities:
 * - Transform domain model fields for UI display
 * - Compute derived fields like title and formatted fee
 * - Expose structured sections and lists for components
 *
 * Author: Samano CASTRE
 * Date: August 3, 2025
 */
export class MandateViewModel {
    public model!: MandateModel;
    public id: number;
    private _title!: string;
    public operationType!: string;

    /** The label of the mandate type */
    public mandateType!: string;
    public feeRate!: string;
    public applicableVat!: string;
    public startAt!: string;
    public endAt!: string;
    public notes!: string;
    public propertyCategory!: string;
    public agent!: string;
    public referrer!: string;
    public status!: string;
    public audit!: AuditViewModel;
    private _documentMap?: RecordMap<DocumentModel>;
    public isDeleted !: boolean;
    public cardClass!: string;
    public cardHeaderTitle!: string;


    public agencyComHt: string;
    public totalPriceHt: string;


    public agencyComTTC: string;
    public totalPriceTTC: string;

    public needVM: NeedViewModel | null;
    public propertyVM: PropertyViewModel | null;
    public documentId?: number | null;


    /**
     * Constructor
     *
     * Initializes the ViewModel using the provided parameters:
     * - Formats dates and numbers
     * - Builds human-readable labels
     * - Computes display title
     * - Prepares the audit trail
     *
     * @param params Object containing the mandate, view enums, and associated document list
     */
    public constructor(private params: MandateVMParams) {

        const { viewEnums, mandate, documentMap, accessRight, NeedEnums, PropertyEnums } = this.params;

        this._documentMap = documentMap;
        this.model = mandate;

        this.audit = new AuditViewModel(mandate as AuditModel);
        this.id = mandate?.id ?? 0;
        this.feeRate = SoNumber.toFR(mandate?.feeRate);
        this.applicableVat = SoNumber.toFR(mandate?.applicableVat);

        this.notes = mandate?.notes ?? "";
        this.referrer = mandate?.referrer ?? "";

        this.needVM = mandate.need ? new NeedViewModel({
            need: mandate.need,
            viewEnums: NeedEnums,
            isDisplaying: false,
            accessRight
        }) : null;

        this.propertyVM = mandate.property ? new PropertyViewModel({
            property: mandate.property,
            viewEnums: PropertyEnums,
            isDisplaying: false,
            accessRight,
            documentMap
        }
        ) : null;

        this.startAt = DateFormatHelper.toLocaleDateString(mandate?.startAt);
        this.endAt = DateFormatHelper.toLocaleDateString(mandate?.endAt);

        this.agent = formatName(mandate?.agent?.person);

        this.operationType = AppEnumHelper.getLabel(viewEnums.OperationTypeEnum, mandate?.operationType);

        this.mandateType = AppEnumHelper.getLabel(viewEnums.MandateTypeEnum, mandate?.mandateType);


        this._title = "Mandat N° " + this.id + " - " + this.operationType.toUpperCase();
        this.isDeleted = !!params.mandate?.deletedAt;
        this.cardClass = this.params.isDisplaying ? 'active' : '';

        this.cardHeaderTitle = `${mandate?.mandateType ? this.mandateType : this.operationType} : (ID - ${this.id})`;

        this.agencyComHt = SoNumber.toFR(this.model.agencyComHt);
        this.totalPriceHt = SoNumber.toFR(this.model.totalPriceHt);

        this.agencyComTTC = SoNumber.toFR(SoVAT.toTTC(this.model.agencyComHt, this.model.applicableVat));

        this.totalPriceTTC = SoNumber.toFR(SoVAT.toTTC(this.model.totalPriceHt, this.model.applicableVat));
        this.documentId = this._documentMap?.lastById()?.id ?? null;

    }

    hasDocument(): boolean {

        return this._documentMap ? !this._documentMap.isEmpty() : false;
    }

    documentMap() : RecordMap<DocumentModel>|undefined {
        return this._documentMap;
    }

    get docCategory(): string {
        return AppEnumHelper.getLabel(this.params.DocumentEnums.DocumentCategoryEnum, 'Mandate');
    }

    get docType(): string {
        return this.mandateType;
    }

    /**
     * Returns the most recently added document, or null if none exist.
     */
    getLatestDocument() {
        return this._documentMap?.lastById() ?? null;
    }


    get title() {
        return this._title;
    }

    /**
     * Returns all structured detail sections for UI rendering.
     *
     * Sections include:
     * - Mandate details
     * - Stakeholders
     * - Associated properties
     * - Audit trail
     */
    getAllSections = (): DetailSection[] => {
        const sections: DetailSection[] = [
            {
                sectionFn: this.getMandateSection,
                legend: 'Détails du mandat',
                type: 'box'
            },
            {
                sectionFn: this.getGlobalSection,
                legend: 'Référent & Montants',
                type: 'box'
            },
        ];

        if (this.needVM) {
            sections.push({
                sectionFn: this.needVM.getGlobalSection,
                legend: 'Besoin associé',
                type: 'box'
            },);
        }
        if (this.propertyVM) {
            sections.push({
                sectionFn: this.propertyVM.getGlobalSection,
                legend: 'Bien associé',
                type: 'box'
            },);
        }

        if (!!this.model?.mandateContactLinks?.length) {
            sections.push({
                sectionFn: this.getContactLinksSection,
                legend: 'Mandants associés',
                type: 'list'
            })
        }


        if (this.params.accessRight.canAudit(DOMAIN_TYPES.Mandate)) {
            sections.push({
                sectionFn: this.audit.getAuditTrail,
                legend: 'Traçabilité',
                type: 'box'
            });
        }

        return sections;
    }


    /**
     * Returns mandate-specific detail items.
     */
    getMandateSection = () => {
        return [
            { label: "N° Mandat", value: this.id },
            { label: "Type d'opération", value: this.operationType },
            { label: "Type de mandat", value: this.mandateType },
            { label: "Date de début", value: this.startAt },
            { label: "Date de fin", value: this.endAt },
            { label: "Note", value: this.notes },
        ] as DetailItem[];
    }

    /**
     * Returns stakeholders (contact, agent, referrer) detail items.
     */
    getGlobalSection = () => {

        return [
            { label: "Référent", value: this.agent },
            { label: "Apporteur", value: this.referrer },
            { label: "Honoraires", value: this.feeRate + '%' },
            { label: "TVA", value: this.applicableVat + '%' },
            { label: "Com. Agence", value: this.agencyComHt },
            { label: "Montant", value: this.totalPriceHt },
            { label: "Com. Agence (TTC)", value: this.agencyComTTC },
            { label: "Montant (TTC)", value: this.totalPriceTTC },

        ] as DetailItem[];
    }


    /**
     * Returns associated property details as a structured list.
     *
     * @returns Object with headers and rows for table rendering
     */
    getContactLinksSection = () => {
        const headers = ["N°", "Nom", "Tél", "Date"];

        const rows = [] as Row[];
        (this.model?.mandateContactLinks ?? [])
            .forEach(
                mcLink => {
                    const contact = mcLink.contact;
                    if (contact) {
                        rows.push(
                            {
                                items: [
                                    { label: String(contact.id) },
                                    { label: formatName(contact.person) },
                                    { label: contact.person?.phone1 ?? contact.person?.phone2 ?? "" },
                                    { label: DateFormatHelper.toLocaleString(mcLink.createdAt) }
                                ]
                            }
                        );
                    }
                }
            );

        return { headers, rows };
    }

    compareTo(other: MandateViewModel): number {
        return dateCompare(this.model.createdAt ?? "", other.model.createdAt ?? "");
    }
}